import moment from "moment";
import { z } from "zod";

import { validateEircode } from "utils/validators";

export const generalInformationFormSchema = z.object({
  hospital: z
    .string({ required_error: "You must select a hospital" })
    .min(1, "You must select a hospital"),
  referralType: z.string().min(1, "You must select a referral type"),
});

export const patientDetailsFormSchema = z.object({
  gpOutofHoursNumber: z.string().min(1, "GP Out of Hours Number is required"),
  familyName: z.string().min(1, "Family Name is required"),
  firstName: z.string().min(1, "First Name is required"),
  title: z.string(),
  gender: z.string().min(1, "Gender is required"),
  dateOfBirth: z
    .string()
    .min(1, "Date of Birth is required")
    .refine(
      (val) => {
        return moment(val, ["DD-MM-YYYY", moment.ISO_8601]).isValid();
      },
      {
        message: "Invalid Date of Birth",
      }
    ),
  firstLanguage: z.string().min(1, "First Language is required"),
  wheelchairAssistance: z.string().min(1, "Wheelchair Assistance is required"),
  interpreterRequired: z.string().min(1, "Interpreter Required is required"),
  mothersMaidenName: z.string(),
  streetAddress: z.string().min(1, "Street Address is required"),
  addressLine2: z.string().min(1, "Address line 2 is required"),
  addressLine3: z.string(),
  addressLine4: z.string(),
  eircode: z
    .string()
    .refine(
      (val) => !val || val.trim() === "" || validateEircode(val).isValid,
      {
        message: (val) => {
          if (!val || val.trim() === "") return "";
          return validateEircode(val).error || "Invalid Eircode format";
        },
      }
    ),
  mobile: z.string().min(1, "Mobile is required"),
  phone: z.string(),
  email: z.string(),
});

export const laboratoryInvestigationSchema = z.object({
  title: z.string(),
  date: z.string(),
  result: z.string(),
});

export const radiologyInvestigationSchema = z.object({
  title: z.string(),
  date: z.string(),
  result: z.string(),
});

const anticoagulantMedicationsSchema = z.object({
  name: z.string(),
  dosage: z.string(),
});

export const referralFormSchema = (isLungReferral, isProstate) =>
  z.object({
    referralPriority: isLungReferral
      ? z.string()
      : z.string().min(1, "Referral Priority is required"),
    preferredConsultantFamilyName: z.string(),
    preferredConsultantFirstName: z.string(),
    reasonForReferral:
      isLungReferral || isProstate
        ? z.string()
        : z.string().min(1, "Reason for Referral is required"),
    historyOfPresentingComplains: z
      .string()
      .refine(
        (value) => value.trim().length > 0,
        "History of Presenting Complaints is required"
      ),
    previousHospitalAttendance: z.string(),
    historyOfPastIllness: z.string(),
    historyOfSurgicalProcedures: z.string(),
    allergiesAdverseMedicationEvents: z.string(),
    relevantFamilyHistory: z.string(),
    additionalInformation: z.string(),
    currentMedication: z.string(),
    patientOnAnticoagulants:
      isProstate || isLungReferral
        ? z.string()
        : z.string().min(1, "Select an option"),
    anticoagulantMedications: z
      .array(anticoagulantMedicationsSchema)
      .optional(),
    nextOfKin: z.string(),
    unitsOfAlcohol: z.string(),
    numberOfCigarettes: z.string(),
    yearsSmoking: z.string(),
    smoker: isLungReferral
      ? z.string().min(1, "Smoker is required")
      : z.string().optional(),
    drinker: z.string().min(1, "Drinker is required"),
    pulse: z.string(),
    bp1: z.string(),
    bp2: z.string(),
    bodyHeight: z.string(),
    weight: z.string(),
    bmi: z.string(),
    clinicalExamination: z.string(),
    laboratoryInvestigation: z.array(z.any()).optional(),
    radiologyInvestigation: z.array(z.any()).optional(),
    fileAttachment: z.array(z.number()).default([]),
  });

export const prostateAdditionalInfoFormSchema = z
  .object({
    psaTests: z
      .array(
        z.object({
          psa: z
            .string()
            .min(1, "PSA value is required")
            .regex(/^\d*\.?\d*$/, "PSA must be a valid number"),
          date: z.string().min(1, "Date is required"),
        })
      )
      .min(1, "At least one PSA test is required")
      .default([]),
    hasSeenUrologist: z
      .string()
      .min(1, "Previous Seen by Urologist is required"),
    hasSeenUrologistConsultant: z.string().optional(),
    dateOfUrologyConsult: z.string().optional().nullable(),
    hasSeenUrologistLocations: z.string().optional(),
    hasPreviousBiopsy: z
      .string()
      .min(1, "Previous Prostate Biopsy is required"),
    hasPreviousBiopsyHospital: z.string().optional(),
    hasPreviousBiopsyDate: z.string().nullable().optional(),
    hasPreviousBiopsyResult: z.string().optional(),
    digitalRectalExam: z.string(),
    otherClinicalExam: z.string(),
    resultOfUrinalysis: z.string(),
    otherLabInvestigations: z.string(),
    otherRadInvestigations: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.hasSeenUrologist === "yes") {
        return (
          data.dateOfUrologyConsult && data.dateOfUrologyConsult.length > 0
        );
      }
      return true;
    },
    {
      message: "Date is required when previously seen by urologist",
      path: ["dateOfUrologyConsult"],
    }
  )
  .refine(
    (data) => data.hasPreviousBiopsy !== "yes" || data.dateOfBiopsy !== null,
    {
      error: "Date is required when there was a previous biopsy",
      path: ["dateOfBiopsy"],
    }
  );

const isRadInvestigationCommentRequired = (investigation, result) =>
  investigation !== "not-done" && result === "Abnormal";

export const lungsAdditionalInfoFormSchema = z
  .object({
    haemoptysis: z.boolean().optional(),
    clubbing: z.boolean().optional(),
    lymphadenopathy: z.boolean().optional(),
    hepatosplenomegaly: z.boolean().optional(),
    historyOfAsthma: z.boolean().optional(),
    historyOfRentalInsufficiency: z.boolean().optional(),
    historyOfAllergyToContrastDye: z.boolean().optional(),

    otherPersistentUnexplainedSymptoms: z.string().optional(),
    otherClinicalFindings: z.string().optional(),
    otherRadInvestigations: z.string().optional(),
    hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer: z
      .string()
      .min(1, "Select an option"),
    lungLabInvestigation: z.string().optional(),
    specifiedChestSigns: z.string().optional(),

    radInvestigation1: z.string().optional(),
    radInvestigation1Result: z.string().optional().nullable(),
    radInvestigation1Comment: z.string().optional().nullable(),
    radInvestigation2: z.string().optional(),
    radInvestigation2Result: z.string().optional().nullable(),
    radInvestigation2Comment: z.string().optional().nullable(),

    currentMedication: z.string(),
    patientOnAnticoagulants: z.string().min(1, "Select an option"),
    patientOnAspirin: z.string().optional().nullable(),
    patientOnPlavix: z.string().optional().nullable(),
    patientOnWarfarin: z.string().optional().nullable(),
    aspirinDetails: z.string().optional().nullable(),
    plavixDetails: z.string().optional().nullable(),
    warfarinDetails: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      isRadInvestigationCommentRequired(
        data.radInvestigation1,
        data.radInvestigation1Result
      ) &&
      !data.radInvestigation1Comment?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Comment is required when the result is abnormal",
        path: ["radInvestigation1Comment"],
      });
    }

    if (
      isRadInvestigationCommentRequired(
        data.radInvestigation2,
        data.radInvestigation2Result
      ) &&
      !data.radInvestigation2Comment?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Comment is required when the result is abnormal",
        path: ["radInvestigation2Comment"],
      });
    }
  });
